import { autoDownloadPresignedUrl, sanitizeFileName } from './presignedDownloadUtils';

export const CHRONOLOGY_EXHIBITS_DOWNLOAD_TYPE = 'chronology_exhibits';
export const CHRONOLOGY_EXHIBIT_DOWNLOAD_NOTIFICATION = 'get_chronology_exhibit_content_message';

/**
 * Finds an In Progress chronology exhibits tray download for a chronology id.
 * @param {Array} activeDownloads
 * @param {string} chronologyId
 * @returns {string|undefined}
 */
export const findActiveChronologyExhibitDownloadId = (activeDownloads, chronologyId) => {
  if (!chronologyId) {
    return undefined;
  }
  return activeDownloads?.find(
    (download) =>
      download.type === CHRONOLOGY_EXHIBITS_DOWNLOAD_TYPE && download.chronologyId === chronologyId && download.status === 'In Progress'
  )?.id;
};

/**
 * Processes a chronology exhibit ZIP WebSocket completion message.
 * Pure side-effect coordinator used by NavbarContext (and unit tests).
 *
 * @returns {'ignored'|'duplicate'|'success'|'failed'|'success_without_url'}
 */
export const processChronologyExhibitDownloadMessage = ({
  data,
  activeDownloads,
  processedSks,
  updateFolderDownload,
  completeFolderDownload,
  removeActiveDownload,
}) => {
  if (data?.notification_type !== CHRONOLOGY_EXHIBIT_DOWNLOAD_NOTIFICATION) {
    return 'ignored';
  }

  const messageSk = data?.sk;
  if (messageSk && processedSks?.has(messageSk)) {
    return 'duplicate';
  }

  const content = data?.body?.['chronology-exhibit-content'];
  if (!content) {
    return 'ignored';
  }

  const { chronology_id: chronologyId, task_status: taskStatus, message, presigned_url: presignedUrl } = content;
  const fileName = sanitizeFileName(content.file_name);
  const downloadId = findActiveChronologyExhibitDownloadId(activeDownloads, chronologyId);

  if (!downloadId) {
    return 'ignored';
  }

  if (messageSk && processedSks) {
    processedSks.add(messageSk);
  }

  if (taskStatus === 'Success') {
    updateFolderDownload?.(downloadId, {
      status: 'completed',
      zipFile: {
        id: downloadId,
        fileName,
        presignedUrl,
      },
      message,
    });

    if (presignedUrl && autoDownloadPresignedUrl(presignedUrl)) {
      completeFolderDownload?.(downloadId);
      return 'success';
    }

    updateFolderDownload?.(downloadId, {
      status: 'failed',
      error: message || 'Download failed',
    });
    removeActiveDownload?.(downloadId);
    return 'success_without_url';
  }

  if (taskStatus === 'Failed') {
    updateFolderDownload?.(downloadId, {
      status: 'failed',
      error: message || 'Download failed',
    });
    removeActiveDownload?.(downloadId);
    return 'failed';
  }

  return 'ignored';
};
